import React, { FC } from 'react';
import { FormikHelpers, FormikProps } from 'formik';
import { observer } from 'mobx-react';

import {
    AppSection,
    AppSectionHeader,
    InputText,
    Form,
    AppFooter,
    Button,
    ButtonPrimary,
    ActionList,
    ButtonOutlinePrimary,
    InputSelect
} from '../../../../components-v2/shared';

import useStore from '../../../../store/useStore';
import DefaultRoutes from '../../../../routes/DefaultRoutes';
import { Crm } from '../../../../types/Crm';
import { crmConfigTypes, unsavedChangesContainerSelectorDefault } from '../../../../constants';
import { CrmRuleConditionSubjects, CrmRulePredicatePicklists } from '../helpers/FormHelper';
import { JSONWithRuleset, RuleEditor } from '../../../RuleEditor/RuleEditor';
import { RuleAction } from '../../../../types';

export interface RentDynamicsFormProps {
    initialValues: any;
    onSubmit: (values: Crm, actions: FormikHelpers<Crm>, crmType: string) => Promise<void>;
    onValidate: (values: Crm, crmType: string) => Promise<void>;
    enableReinitialize?: boolean;
}

interface RentDynamicsCrm extends Crm {
    configuration: {
        client_name: string;
        community_name: string;
        community_id: number;
        community_group_id: number;
        access_key: string;
        secret_access_key: string;
        environment: 'production' | 'sandbox';
        ad_source_id: number;
    };
}

const ruleActions: Array<RuleAction> = [];

export const RentDynamicsForm: FC<RentDynamicsFormProps> = observer(({ onSubmit, onValidate, ...props }) => {
    const { store } = useStore();
    const { router } = store;
    const { companyId } = router.params;

    const handleCancel = () => router.goTo(DefaultRoutes.CrmSelectType, { companyId }, store);

    const handleValidate = async (formikProps: FormikProps<RentDynamicsCrm>) => {
        await onValidate(formikProps.values, crmConfigTypes.rent_dynamics);
    };

    const handleSubmit = async (values: RentDynamicsCrm, actions: FormikHelpers<RentDynamicsCrm>) => {
        await onSubmit(values, actions, crmConfigTypes.rent_dynamics);
    };

    return (
        <Form
            {...props}
            onSubmit={handleSubmit}
            confirmUnsavedChanges
            unsavedChangesConfig={{
                containerQuerySelectorAll: unsavedChangesContainerSelectorDefault
            }}
        >
            {(formikProps: FormikProps<RentDynamicsCrm>) => {
                return (
                    <>
                        <AppSection className="field-group-content">
                            <div className="field-group-col">
                                <AppSectionHeader title="CRM Configuration: Rent Dynamics" />
                                <InputText
                                    name="name"
                                    label="Name"
                                    helperText="Unique name for this CRM Configuration"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 64 }}
                                />
                                <AppSectionHeader title="Connection Settings" />
                                <InputText
                                    name="configuration.access_key"
                                    label="Access Key"
                                    helperText="Access Key"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 250 }}
                                />
                                <InputText
                                    name="configuration.secret_access_key"
                                    label="Secret Access Key"
                                    helperText="Secret Access Key"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 250 }}
                                />
                                <InputSelect
                                    name="configuration.environment"
                                    label="Environment"
                                    helperText="Environment"
                                    required={true}
                                    formikProps={formikProps}
                                    options={[
                                        { value: 'production', label: 'Production' },
                                        { value: 'sandbox', label: 'Sandbox' }
                                    ]}
                                    optionValueKey="value"
                                    optionLabelKey="label"
                                />
                            </div>
                            <div className="field-group-col">
                                <AppSectionHeader title="Community Options" />

                                <InputText
                                    name="configuration.client_name"
                                    label="Client Name"
                                    helperText="Rent Dynamics Client Name"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.community_name"
                                    label="Community Name"
                                    helperText="Rent Dynamics Community Name"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.community_id"
                                    label="Community ID"
                                    helperText="Rent Dynamics Community ID"
                                    required={true}
                                    type="number"
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.community_group_id"
                                    label="Community Group ID"
                                    helperText="Rent Dynamics Community Group ID"
                                    type="number"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                                <InputText
                                    name="configuration.ad_source_id"
                                    label="Ad Source ID"
                                    helperText="Rent Dynamics Ad Source ID"
                                    type="number"
                                    required={true}
                                    formikProps={formikProps}
                                    inputProps={{ maxLength: 255 }}
                                />
                            </div>
                        </AppSection>
                        <RuleEditor
                            formikProps={formikProps as FormikProps<JSONWithRuleset>}
                            availableRuleActions={ruleActions}
                            availableRuleConditionSubjects={CrmRuleConditionSubjects}
                            predicatePicklists={CrmRulePredicatePicklists}
                        />
                        <AppFooter sticky={true}>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button onClick={handleCancel} data-lc-trigger-unsaved-changes={true}>
                                        Cancel
                                    </Button>
                                )}
                                <ButtonOutlinePrimary onClick={() => handleValidate(formikProps)}>
                                    Validate
                                </ButtonOutlinePrimary>
                                <ButtonPrimary type="submit" disabled={!formikProps.dirty || formikProps.isSubmitting}>
                                    {formikProps.isSubmitting ? 'Saving...' : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </>
                );
            }}
        </Form>
    );
});
